import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { LearningCenterShare } from '@/hooks/useLearningCenterShares';

interface LearningCenterShareDialogState {
  isOpen: boolean;
  editingShare: LearningCenterShare | null;
  onSuccessCallback?: () => void;
}

interface LearningCenterShareDialogContextType {
  state: LearningCenterShareDialogState;
  openCreateDialog: (onSuccess?: () => void) => void;
  openEditDialog: (share: LearningCenterShare, onSuccess?: () => void) => void;
  closeDialog: () => void;
}

const LearningCenterShareDialogContext = createContext<LearningCenterShareDialogContextType | undefined>(undefined);

export function LearningCenterShareDialogProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LearningCenterShareDialogState>({
    isOpen: false,
    editingShare: null,
    onSuccessCallback: undefined,
  });

  const openCreateDialog = useCallback((onSuccess?: () => void) => {
    setState({
      isOpen: true,
      editingShare: null,
      onSuccessCallback: onSuccess,
    });
  }, []);

  const openEditDialog = useCallback((share: LearningCenterShare, onSuccess?: () => void) => {
    setState({
      isOpen: true,
      editingShare: share,
      onSuccessCallback: onSuccess,
    });
  }, []);

  const closeDialog = useCallback(() => {
    setState({
      isOpen: false,
      editingShare: null,
      onSuccessCallback: undefined,
    });
  }, []);

  return (
    <LearningCenterShareDialogContext.Provider value={{ state, openCreateDialog, openEditDialog, closeDialog }}>
      {children}
    </LearningCenterShareDialogContext.Provider>
  );
}

export function useLearningCenterShareDialog() {
  const context = useContext(LearningCenterShareDialogContext);
  if (!context) {
    throw new Error('useLearningCenterShareDialog must be used within a LearningCenterShareDialogProvider');
  }
  return context;
}
